import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Chip, CircularProgress, Alert, TextField, MenuItem,
  Grid, Stack, Button,
  Snackbar, Tooltip, Pagination, InputAdornment,
} from '@mui/material';
import {
  Eye, Search, CheckCircle, XCircle, ShieldCheck, Ban, Wallet, FileText, Users, Clock,
  ExternalLink, AlertTriangle,
} from 'lucide-react';
import {
  fetchPartners, fetchPartnerStats, fetchPartnerDetail, reviewKyc,
  updatePartnerStatus, adjustPartnerWallet, fetchKycDocUrl,
} from '../utils/partnerApi';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import { TableLoadingSkeleton } from '../components/common/LoadingState';
import StatusBadge from '../components/common/StatusBadge';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from '../components/common/modal';
import ExportTableButton from '../components/common/ExportTableButton';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const KYC_FIELDS = ['aadhaar_doc', 'pan_doc', 'photo'];
const KYC_LABELS = { aadhaar_doc: 'Aadhaar Card', pan_doc: 'PAN Card', photo: 'Photo' };

// Inline preview tile for one KYC document. Renders images directly and PDFs in
// an embedded frame; clicking opens the full document in a new tab.
const KycDocPreview = ({ label, doc }) => {
  const isPdf = doc?.type?.includes('pdf');
  return (
    <Box sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>{label}</Typography>
      <Box
        onClick={() => doc?.url && window.open(doc.url, '_blank', 'noopener')}
        sx={{
          position: 'relative', height: 140, borderRadius: 2, overflow: 'hidden',
          border: '1px solid', borderColor: 'divider', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.neutral' : 'grey.50',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: doc?.url ? 'pointer' : 'default',
          '&:hover .kyc-zoom': { opacity: doc?.url ? 1 : 0 },
        }}
      >
        {!doc || doc.loading ? (
          <CircularProgress size={22} />
        ) : doc.error ? (
          <Stack alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
            <AlertTriangle size={20} />
            <Typography variant="caption">Not available</Typography>
          </Stack>
        ) : isPdf ? (
          <>
            <Box component="embed" src={doc.url} type="application/pdf" sx={{ width: '100%', height: '100%', pointerEvents: 'none' }} />
            <Chip icon={<FileText size={13} />} label="PDF" size="small" sx={{ position: 'absolute', bottom: 6, left: 6, height: 22, fontWeight: 700 }} />
          </>
        ) : (
          <Box component="img" src={doc.url} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        )}
        {doc?.url && (
          <Box className="kyc-zoom" sx={{ position: 'absolute', top: 6, right: 6, opacity: 0, transition: 'opacity .15s', bgcolor: 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: 1, p: '3px 6px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ExternalLink size={13} />
            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>Open</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const StatCard = ({ icon, label, value, color = 'primary.main' }) => (
  <Card sx={{ p: 2.5, borderRadius: 2.5, display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: (theme) => `${theme.palette[color]?.main || color}16`, color: (theme) => theme.palette[color]?.main || color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5, display: 'block' }}>{label}</Typography>
    </Box>
  </Card>
);

const PartnersView = () => {
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', kyc_status: '', status: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const loadStats = useCallback(async () => {
    try {
      const res = await fetchPartnerStats();
      setStats(res.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 25 };
      if (filters.search) params.search = filters.search;
      if (filters.kyc_status) params.kyc_status = filters.kyc_status;
      if (filters.status) params.status = filters.status;
      const res = await fetchPartners(params);
      setPartners(res.data.partners || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadPartners(); }, [loadPartners]);

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetail(null);
    try {
      const res = await fetchPartnerDetail(id);
      setDetail(res.data);
    } catch (e) {
      notify('Failed to load partner', 'error');
      setDetailOpen(false);
    }
  };

  const afterMutation = () => {
    loadPartners();
    loadStats();
  };

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="Partner Program"
        subtitle="Manage affiliates, KYC verification, earnings wallets, and partner statuses."
        count={stats?.totalPartners}
        countLabel="partners"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Growth', href: '/partners' },
          { label: 'Partners' }
        ]}
        secondaryAction={
          <ExportTableButton
            data={partners}
            total={total}
            onExportAll={async () => {
              const params = { all: true };
              if (filters.search) params.search = filters.search;
              if (filters.kyc_status) params.kyc_status = filters.kyc_status;
              if (filters.status) params.status = filters.status;
              const res = await fetchPartners(params);
              return res.data?.partners || [];
            }}
            columns={[
              { label: 'Partner Name', key: 'full_name' },
              { label: 'Phone', key: 'phone' },
              { label: 'Email', key: 'email' },
              { label: 'Partner Code', key: 'partnerCode' },
              { label: 'KYC Status', key: 'kyc_status' },
              { label: 'Total Referred', key: 'total_referred_sellers' },
              { label: 'Paid Sellers', key: 'paid_sellers_count' },
              { label: 'Wallet Balance (₹)', key: 'wallet_balance' },
              { label: 'Status', key: 'status' },
            ]}
            filename="partners"
          />
        }
      />

      {/* Stats */}
      {stats && (
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}><StatCard icon={<Users size={22} />} label="Total Partners" value={stats.totalPartners} color="primary" /></Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}><StatCard icon={<Clock size={22} />} label="Pending KYC" value={stats.pendingKyc} color="warning" /></Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}><StatCard icon={<Wallet size={22} />} label="Pending Payouts" value={stats.pendingPayouts} color="secondary" /></Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}><StatCard icon={<ShieldCheck size={22} />} label="Commission Paid" value={inr(stats.totalCommissionPaid)} color="success" /></Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ p: 2, borderRadius: 2.5, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              fullWidth size="small" placeholder="Search name, phone, email, code…"
              value={filters.search}
              onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, search: e.target.value })); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search size={18} /></InputAdornment> }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3.5 }}>
            <TextField fullWidth size="small" select label="KYC Status" value={filters.kyc_status}
              onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, kyc_status: e.target.value })); }}>
              <MenuItem value="">All KYC</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3.5 }}>
            <TextField fullWidth size="small" select label="Status" value={filters.status}
              onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, status: e.target.value })); }}>
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
              <MenuItem value="banned">Banned</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Partner', 'Code', 'KYC', 'Referred / Paid', 'Wallet', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} align={h === 'Actions' ? 'right' : 'left'}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingSkeleton rows={10} cols={7} />
              ) : partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                    <EmptyState
                      icon={Users}
                      title="No partners found"
                      description={filters.search || filters.kyc_status || filters.status ? "No partners match the current search or filters. Try adjusting them." : "Affiliate partners and referrers will appear here once registered."}
                      actionLabel={filters.search || filters.kyc_status || filters.status ? "Reset Filters" : undefined}
                      onAction={filters.search || filters.kyc_status || filters.status ? () => { setPage(1); setFilters({ search: '', kyc_status: '', status: '' }); } : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                partners.map((p) => (
                  <TableRow key={p._id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{p.full_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.phone} · {p.email}</Typography>
                    </TableCell>
                    <TableCell><Chip label={p.partnerCode} size="small" sx={{ fontWeight: 700 }} /></TableCell>
                    <TableCell>
                      <StatusBadge status={p.kyc_status} />
                    </TableCell>
                    <TableCell>{p.total_referred_sellers || 0} / {p.paid_sellers_count || 0}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{inr(p.wallet_balance)}</TableCell>
                    <TableCell>
                      <StatusBadge status={p.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton aria-label="View details" size="small" onClick={() => openDetail(p._id)}><Eye size={18} /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        )}
      </Card>

      <PartnerDetailDialog
        open={detailOpen}
        detail={detail}
        onClose={() => setDetailOpen(false)}
        notify={notify}
        onChanged={(fresh) => { setDetail(fresh); afterMutation(); }}
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ fontWeight: 600 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// ── Detail dialog: KYC docs + review + status + wallet adjust ─────────────────
const PartnerDetailDialog = ({ open, detail, onClose, notify, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [adjustAmt, setAdjustAmt] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const p = detail?.partner;
  // KYC document previews: { [field]: { url, type, loading, error } }
  const [docs, setDocs] = useState({});

  useEffect(() => { setRejectReason(''); setAdjustAmt(''); setAdjustNote(''); }, [detail]);

  // Load the three KYC documents as object URLs when a partner opens, so the
  // reviewer can eyeball them inline before approving. URLs are revoked on
  // unmount / partner change to avoid leaking blob memory.
  useEffect(() => {
    const pid = p?._id;
    if (!pid) { setDocs({}); return undefined; }
    let cancelled = false;
    const created = [];
    setDocs({
      aadhaar_doc: { loading: true }, pan_doc: { loading: true }, photo: { loading: true },
    });
    KYC_FIELDS.forEach(async (field) => {
      try {
        const { url, type } = await fetchKycDocUrl(pid, field);
        if (cancelled) { URL.revokeObjectURL(url); return; }
        created.push(url);
        setDocs((d) => ({ ...d, [field]: { url, type, loading: false } }));
      } catch (e) {
        if (!cancelled) setDocs((d) => ({ ...d, [field]: { loading: false, error: true } }));
      }
    });
    return () => { cancelled = true; created.forEach((u) => URL.revokeObjectURL(u)); };
  }, [p?._id]);

  const doReview = async (action) => {
    if (action === 'reject' && !rejectReason.trim()) { notify('Enter a rejection reason', 'error'); return; }
    setBusy(true);
    try {
      const res = await reviewKyc(p._id, action, rejectReason);
      notify(`KYC ${action}d`);
      onChanged({ ...detail, partner: res.data.partner });
    } catch (e) { notify('Action failed', 'error'); } finally { setBusy(false); }
  };

  const doStatus = async (status) => {
    setBusy(true);
    try {
      const res = await updatePartnerStatus(p._id, { status });
      notify(`Partner ${status}`);
      onChanged({ ...detail, partner: res.data.partner });
    } catch (e) { notify('Action failed', 'error'); } finally { setBusy(false); }
  };

  const doAdjust = async () => {
    const amt = Math.round(Number(adjustAmt));
    if (!amt) { notify('Enter a non-zero amount', 'error'); return; }
    setBusy(true);
    try {
      const res = await adjustPartnerWallet(p._id, amt, adjustNote);
      notify('Wallet adjusted');
      onChanged({ ...detail, partner: { ...p, wallet_balance: res.data.wallet_balance } });
      setAdjustAmt(''); setAdjustNote('');
    } catch (e) { notify('Adjustment failed', 'error'); } finally { setBusy(false); }
  };

  return (
    <AdminDialog open={open} onClose={onClose} maxWidth="md">
      <AdminDialogHeader
        title="Partner Details"
        subtitle="Comprehensive affiliate profile, KYC documents, and earnings history"
        icon={Users}
        onClose={onClose}
      />
      <AdminDialogContent dividers>
        {!detail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
        ) : (
          <Stack spacing={2.5}>
            {/* Identity */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{p.full_name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{p.phone} · {p.email}</Typography>
                <Typography variant="body2" color="text.secondary">{[p.city, p.state].filter(Boolean).join(', ')}</Typography>
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Chip label={`Code: ${p.partnerCode}`} size="small" sx={{ fontWeight: 700 }} />
                  <StatusBadge status={p.kyc_status} />
                  <StatusBadge status={p.status} />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2">Wallet: <b>{inr(p.wallet_balance)}</b></Typography>
                  <Typography variant="body2">Total earned: <b>{inr(p.total_earned)}</b> · Withdrawn: <b>{inr(p.total_withdrawn)}</b></Typography>
                  <Typography variant="body2">Referred: <b>{detail.referredCount}</b> · Paid: <b>{detail.paidCount}</b></Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>Aadhaar: <b>{p.aadhaar_number}</b> · PAN: <b>{p.pan_number}</b></Typography>
                </Stack>
              </Grid>
            </Grid>

            {/* KYC docs — inline previews to verify before approving */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>KYC Documents</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
                {KYC_FIELDS.map((f) => (
                  <KycDocPreview key={f} label={KYC_LABELS[f]} doc={docs[f]} />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Click a document to open it full-size in a new tab.
              </Typography>
            </Box>

            {/* KYC review */}
            {p.kyc_status !== 'approved' && (
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,152,0,0.06)', border: '1px solid rgba(255,152,0,0.2)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Review KYC</Typography>
                <TextField fullWidth size="small" label="Rejection reason (required to reject)" value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)} sx={{ mb: 1.5 }} />
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Button variant="contained" color="success" startIcon={<CheckCircle size={18} />} disabled={busy} onClick={() => doReview('approve')} sx={{ textTransform: 'none', fontWeight: 700, flex: { xs: 1, sm: 'none' } }}>Approve</Button>
                  <Button variant="outlined" color="error" startIcon={<XCircle size={18} />} disabled={busy} onClick={() => doReview('reject')} sx={{ textTransform: 'none', fontWeight: 700, flex: { xs: 1, sm: 'none' } }}>Reject</Button>
                </Stack>
                {p.kyc_status === 'rejected' && p.kyc_rejection_reason && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>Rejected: {p.kyc_rejection_reason}</Typography>
                )}
              </Box>
            )}

            {/* Status + wallet */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Account Status</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant={p.status === 'active' ? 'contained' : 'outlined'} startIcon={<ShieldCheck size={16} />} disabled={busy} onClick={() => doStatus('active')} sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>Active</Button>
                  <Button size="small" color="warning" variant={p.status === 'suspended' ? 'contained' : 'outlined'} disabled={busy} onClick={() => doStatus('suspended')} sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>Suspend</Button>
                  <Button size="small" color="error" variant={p.status === 'banned' ? 'contained' : 'outlined'} startIcon={<Ban size={16} />} disabled={busy} onClick={() => doStatus('banned')} sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>Ban</Button>
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Wallet Adjustment</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                  <TextField size="small" type="number" label="Amount (±₹)" value={adjustAmt} onChange={(e) => setAdjustAmt(e.target.value)} sx={{ width: { xs: '100%', sm: 130 } }} />
                  <TextField size="small" label="Note" value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)} sx={{ flexGrow: 1 }} />
                  <Button variant="contained" disabled={busy} onClick={doAdjust} sx={{ textTransform: 'none', fontWeight: 700 }}>Apply</Button>
                </Stack>
              </Grid>
            </Grid>

            {/* Recent earnings */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Recent Earnings</Typography>
              <TableContainer sx={{ maxHeight: 220, overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={{ minWidth: 480 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Detail</TableCell>
                      <TableCell sx={{ fontWeight: 800 }} align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.recentCommissions || []).length === 0 ? (
                      <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 3 }}>No earnings yet.</TableCell></TableRow>
                    ) : detail.recentCommissions.map((c) => (
                      <TableRow key={c._id}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{(c.type || '').replace(/_/g, ' ')}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{c.plan_name || c.note || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(c.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        )}
      </AdminDialogContent>
      <AdminDialogActions>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 600 }}>Close</Button>
      </AdminDialogActions>
    </AdminDialog>
  );
};

export default PartnersView;
